import { Fragment, FC, useState, useEffect } from "react";
import {
  AppBar,
  Box,
  Container,
  Divider,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Toolbar,
  Typography,
} from "mui";
import { Link, useLocation } from "react-router-dom";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AccountCircle from "@mui/icons-material/AccountCircle";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";

import projectLogo from "assets/images/login.png";
import styles from "components/Header/Header.module.scss";
import { closeSession } from "store/session/thunks";
import { useDispatch } from "react-redux";
import { useRoles } from "utils/hooks/useRoles";

const Header: FC = () => {
  const location = useLocation();
  const tabPath = location.pathname.split("/")[1];
  const dispatch = useDispatch();

  const { isAdmin } = useRoles();
  const [selectedTab, setSelectedTab] = useState(tabPath);
  const [anchorElProfile, setAnchorElProfile] = useState<null | HTMLElement>(
    null
  );
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);

  const isProfileMenuOpen = Boolean(anchorElProfile);
  const handleChangeTab = (event: React.SyntheticEvent, newTab: string) => {
    setSelectedTab(newTab);
  };

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElProfile(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorElProfile(null);
    setAnchorElNav(null);
  };

  const handleLogout = async () => {
    dispatch(closeSession());
  };

  const pages = [
    {
      label: "Home",
      value: tabPath === "" ? "" : "Providers",
      link: "/Providers",
    },
    {
      label: "Templates",
      value: "Templates",
      link: "/Templates",
    },
  ];

  const accountMenu = [
    {
      label: "Profile",
      link: "/MyProfile",
    },
    {
      label: "Roles Management",
      link: "/RolesManagement",
    },
    {
      label: "Log Out",
      link: "/login",
    },
  ];

  useEffect(() => {
    setSelectedTab(location.pathname.split("/")[1]);
  }, [location]);

  return (
    <Fragment>
      <AppBar position="static" className={styles.appBar}>
        <Container className={styles.toolbarContainer}>
          <Toolbar disableGutters>
            <Box className={styles.logo} onClick={handleOpenNavMenu}>
              <img
                className={styles.headerImage}
                src={projectLogo}
                alt="Project Logo"
              />
            </Box>
            <Menu
              className={styles.navMenu}
              slotProps={{
                paper: {
                  className: styles.paper,
                },
              }}
              anchorEl={anchorElNav}
              open={Boolean(anchorElNav)}
              onClose={handleMenuClose}
            >
              {pages.map(({ label, link }, index) => (
                <Box key={index}>
                  {index !== 0 && (
                    <Box className={styles.dividerWrapper}>
                      <Divider className={styles.divider} />
                    </Box>
                  )}
                  <Link className={styles.link} to={link}>
                    <MenuItem>
                      <Typography variant="body1">{label}</Typography>
                    </MenuItem>
                  </Link>
                </Box>
              ))}
            </Menu>
            <Box className={styles.menuContainer}>
              <Box className={styles.tabsContainer}>
                <Tabs
                  className={styles.tabs}
                  TabIndicatorProps={{
                    className: styles.tabIndicator,
                  }}
                  value={selectedTab}
                  onChange={handleChangeTab}
                >
                  {pages.map(({ label, value, link }, index) => {
                    return (
                      <Tab
                        aria-label={`${label} tab`}
                        className={styles.tab}
                        component={Link}
                        key={index}
                        sx={{ color: "white", display: "block" }}
                        label={
                          <Typography
                            className={
                              selectedTab === value ? styles.selectedTab : ""
                            }
                            variant={selectedTab === value ? "h3" : "body1"}
                          >
                            {label}
                          </Typography>
                        }
                        value={value}
                        to={link}
                      />
                    );
                  })}
                </Tabs>
              </Box>
              <Box className={styles.iconsContainer}>
                <IconButton size="large" color="inherit" disabled>
                  <Badge badgeContent={0} color="error">
                    <NotificationsIcon color="primary" />
                  </Badge>
                </IconButton>
                <IconButton
                  className={styles.avatarButton}
                  aria-controls="mainMenu"
                  onClick={handleProfileMenuOpen}
                >
                  <AccountCircle color="primary" />
                  <KeyboardArrowDownRounded color="primary" />
                </IconButton>
              </Box>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      <Menu
        anchorEl={anchorElProfile}
        className={styles.profileMenu}
        slotProps={{
          paper: {
            sx: {
              minWidth: "185px",
            },
          },
        }}
        id="mainHeader"
        keepMounted
        MenuListProps={{
          sx: {
            padding: 0,
          },
        }}
        open={isProfileMenuOpen}
        onClose={handleMenuClose}
      >
        {accountMenu.map(({ label, link }, index) => {
          const signOut = label === "Log Out";
          return accountMenu[index].label === "Roles Management" && !isAdmin ? null : (
            <Box key={index}>
              {index !== 0 && (
                <Box className={styles.dividerWrapper}>
                  <Divider className={styles.divider} />
                </Box>
              )}
              <Link className={styles.link} to={link}>
                <MenuItem
                  sx={{ padding: "8px 16px" }}
                  onClick={signOut ? handleLogout : () => {}}
                >
                  <Typography variant="body1">{label}</Typography>
                </MenuItem>
              </Link>
            </Box>
          );
        })}
      </Menu>
    </Fragment>
  );
};

export default Header;
